import React from 'react';
import { ChevronLeft, ChevronRight, Ship, Truck, Plane } from 'lucide-react';
import Badge from './Badge';

// ─── Generic table ────────────────────────────────────────────────────────────

/**
 * columns: Array<{ key, label, align?: 'right', render?: (value, row) => ReactNode }>
 * rows:    data array
 * rowKey:  field used as React key (default '_id')
 * loading: show animated skeleton rows
 * emptyState: ReactNode shown when rows is empty and not loading
 * page / total / perPage / onPrev / onNext: optional pagination
 */
export function Table({
  columns = [],
  rows = [],
  rowKey = '_id',
  onRowClick,
  loading = false,
  emptyState,
  page,
  total,
  perPage = 10,
  onPrev,
  onNext,
}) {
  const hasPagination = page != null && total != null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2.5 font-semibold ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <div className="h-4 bg-slate-100 rounded animate-pulse w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row, i) => (
                  <tr
                    key={row[rowKey] ?? i}
                    onClick={() => onRowClick?.(row)}
                    className={`hover:bg-slate-50 transition ${onRowClick ? 'cursor-pointer' : ''}`}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : ''}`}
                      >
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {!loading && rows.length === 0 && emptyState}

      {hasPagination && !loading && rows.length > 0 && (
        <TablePagination
          page={page}
          total={total}
          perPage={perPage}
          onPrev={onPrev}
          onNext={onNext}
        />
      )}
    </div>
  );
}

// ─── Pagination bar ───────────────────────────────────────────────────────────

export function TablePagination({ page, total, perPage = 10, onPrev, onNext }) {
  const pages = Math.ceil(total / perPage);
  return (
    <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-[12px] text-slate-500">
      <span>
        {total} résultats · page {page}/{pages}
      </span>
      <div className="flex gap-1">
        <button
          onClick={onPrev}
          disabled={page <= 1}
          className="h-7 px-2.5 rounded border border-slate-200 bg-white font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronLeft size={13} />
        </button>
        <button
          onClick={onNext}
          disabled={page >= pages}
          className="h-7 px-2.5 rounded border border-slate-200 bg-white font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── ShipmentTable (design-kit / demo use) ────────────────────────────────────

const TRANSPORT_ICON = {
  reefer: { Icon: Ship,  bg: 'bg-sky-50',    text: 'text-sky-800'    },
  route:  { Icon: Truck, bg: 'bg-orange-50', text: 'text-orange-700' },
  air:    { Icon: Plane, bg: 'bg-sky-50',    text: 'text-sky-700'    },
};

function TransportCell({ mode = 'reefer' }) {
  const { Icon, bg, text } = TRANSPORT_ICON[mode] || TRANSPORT_ICON.reefer;
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${bg} ${text}`}>
      <Icon size={13} strokeWidth={1.75} />
    </span>
  );
}

const STATUS_LABEL = {
  preparing: 'En préparation',
  transit:   'En transit',
  delivered: 'Livré',
  incident:  'Incident',
  pending:   'À régler',
  paid:      'Payée',
  overdue30: 'Échue 1–30j',
  overdue60: 'Échue +30j',
  disputed:  'Contestée',
};

const SHIPMENT_COLUMNS = [
  {
    key: 'reference',
    label: 'Référence',
    render: (v) => (
      <span className="font-mono text-[12px] text-primary font-medium">{v}</span>
    ),
  },
  { key: 'client',    label: 'Client',  render: (v) => <span className="font-medium">{v}</span> },
  { key: 'product',   label: 'Produit', render: (v) => <span className="text-slate-600">{v}</span> },
  {
    key: 'departure',
    label: 'Route',
    render: (_, row) => (
      <span className="text-slate-500">{row.departure} → {row.arrival}</span>
    ),
  },
  { key: 'etd', label: 'ETD', render: (v) => <span className="text-slate-600">{v}</span> },
  { key: 'mode',   label: 'Mode',  render: (v) => <TransportCell mode={v} /> },
  { key: 'status', label: 'Statut', render: (v) => <Badge variant={v}>{STATUS_LABEL[v] || v}</Badge> },
  {
    key: 'actions',
    label: 'Actions',
    align: 'right',
    render: (_, row) => (
      <div className="flex items-center justify-end gap-1.5">
        <button className="h-7 px-2.5 rounded border border-slate-200 bg-white text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50">
          Détail
        </button>
        {row.status === 'incident' && (
          <button className="h-7 px-2.5 rounded border border-rose-200 bg-rose-50 text-[11.5px] font-semibold text-rose-700 hover:bg-rose-100">
            Sinistre
          </button>
        )}
      </div>
    ),
  },
];

export function ShipmentTable({ rows = [], onRowClick, page, total, onPrev, onNext }) {
  return (
    <Table
      columns={SHIPMENT_COLUMNS}
      rows={rows}
      onRowClick={onRowClick}
      page={page}
      total={total}
      onPrev={onPrev}
      onNext={onNext}
    />
  );
}

export default Table;
